import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Copy,
  Check,
  RefreshCw,
  AlertCircle,
  ArrowUpRight,
  Sparkles,
  User as UserIcon,
} from 'lucide-react';
import MarkdownContent from './MarkdownContent';
import AIAvatar from '../AIAvatar';
import { ADVISOR_FEATURES, detectFeatures } from './featureChips';
import { useAdvisorContext } from '../../context/PropertyAdvisorContext';
import { fireChatAnalytics } from '../../lib/chatAnalytics';
import { savePendingQuestion } from '../../lib/pendingAdvisorQuestion';
import { cn } from '../../lib/utils';

// Full <followups>...</followups> block. Emitted by the AI at the very end of
// its response and stripped from the visible bubble.
const FOLLOWUPS_RE = /<followups>([\s\S]*?)<\/followups>/i;
// Any partial opening tag streaming in (e.g. "<f", "<foll") that would
// otherwise flash as raw text before the closing tag arrives.
const FOLLOWUPS_PARTIAL_RE = /<f(o(l(l(o(w(u(p(s(>[\s\S]*)?)?)?)?)?)?)?)?)?$/i;

/** Split the assistant's raw content into visible markdown + follow-up chips. */
function splitFollowups(content) {
  if (!content) return { visible: '', followups: [] };
  const closed = content.match(FOLLOWUPS_RE);
  if (closed) {
    const followups = closed[1]
      .split('|')
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 3);
    return { visible: content.replace(FOLLOWUPS_RE, '').trim(), followups };
  }
  // Streaming: hide any partial opening tag so we never flash raw XML.
  const partialIdx = content.search(FOLLOWUPS_PARTIAL_RE);
  if (partialIdx > -1) {
    return { visible: content.slice(0, partialIdx).trimEnd(), followups: [] };
  }
  return { visible: content, followups: [] };
}

/**
 * Single chat bubble. Renders user text as plain, assistant text as Markdown.
 * Shows Copy + Regenerate actions on the last completed assistant message,
 * Retry on any errored message, and tap-to-send follow-up chips beneath the
 * final assistant bubble when the AI emitted a <followups> block.
 * Memoized so streaming a new bubble doesn't re-render completed ones.
 */
function ChatMessage({
  message,
  propertyKey,
  copied,
  isLastAssistant,
  onCopy,
  onRegenerate,
  onRetry,
  onFollowup,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const advisorCtx = useAdvisorContext();
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  // Cross-Page Conversation: render a slim divider row when the Premium
  // Advisor thread carries across pages so the user knows the answers
  // are now grounded on a new view. Renders BEFORE the standard bubble
  // path so the auth pipeline (followups, chips, testids) doesn't run
  // on this pseudo-message.
  if (isSystem && message.kind === 'context_change') {
    return (
      <div
        data-testid="ai-chat-context-change"
        role="status"
        className="my-1 flex items-center gap-2 justify-center text-[11px] text-slate-400 dark:text-slate-500 font-medium"
      >
        <span className="flex-1 h-px bg-slate-200 dark:bg-slate-700/60" />
        <span className="inline-flex items-center gap-1.5 px-2">
          <Sparkles className="w-3 h-3" aria-hidden="true" />
          {message.content}
        </span>
        <span className="flex-1 h-px bg-slate-200 dark:bg-slate-700/60" />
      </div>
    );
  }
  const { visible, followups } = isUser
    ? { visible: message.content, followups: [] }
    : splitFollowups(message.content);
  const showActions = !isUser && !message.streaming && visible && !message.error;
  const showFollowups =
    !isUser && !message.streaming && !message.error && followups.length > 0;
  // Deep-link chips: only after streaming completes so we don't flicker chips
  // in and out as partial feature names arrive.
  const featureIds =
    !isUser && !message.streaming && !message.error && visible
      ? detectFeatures(visible)
      : [];
  const dealId = advisorCtx?.deal_id;
  const handleOpenFeature = (id) => {
    const feature = ADVISOR_FEATURES[id];
    if (!feature) return;
    // Fire analytics BEFORE navigating so we don't lose the event on route
    // change; `keepalive:true` in the fetch keeps the request alive across
    // the unload boundary.
    fireChatAnalytics('advisor_chip_clicked', propertyKey || null, {
      feature_id: id,
      route: feature.route(dealId),
      deal_id: dealId || null,
      message_id: message.id || null,
    });
    navigate(feature.route(dealId));
  };

  return (
    <div
      data-testid={isUser ? 'ai-chat-user-message' : 'ai-chat-assistant-message'}
      className={cn(
        'rp-advisor-msg-in group flex gap-3 w-full',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {isUser ? (
        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-[#12355B] dark:bg-[#45C04A] text-white dark:text-[#0F172A] shadow-sm dark:shadow-emerald-500/10">
          <UserIcon className="w-4 h-4" />
        </div>
      ) : message.error ? (
        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-red-100 dark:bg-red-950 text-red-600 dark:text-red-400">
          <AlertCircle className="w-4 h-4" />
        </div>
      ) : (
        <AIAvatar size={32} />
      )}

      <div className={cn('flex flex-col gap-1 max-w-[85%]', isUser && 'items-end')}>
        <div
          className={cn(
            'px-4 py-2.5 rounded-2xl text-sm leading-relaxed break-words shadow-sm',
            isUser
              ? 'bg-[#12355B] dark:bg-[#45C04A] text-white dark:text-[#0F172A] rounded-tr-sm whitespace-pre-wrap shadow-slate-900/10 dark:shadow-emerald-500/10'
              : message.error
                ? 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-900 rounded-tl-sm shadow-transparent'
                : 'bg-slate-100 dark:bg-slate-800/70 text-slate-800 dark:text-slate-100 rounded-tl-sm dark:border dark:border-slate-700/40 shadow-slate-900/5 dark:shadow-black/20'
          )}
        >
          {isUser || message.error ? (
            <>{message.content}</>
          ) : visible ? (
            <MarkdownContent>{visible}</MarkdownContent>
          ) : null}
          {message.streaming && (
            <span className="inline-block w-1.5 h-4 ml-0.5 -mb-0.5 bg-current opacity-60 animate-pulse" />
          )}
        </div>

        {(showActions || message.error) && (
          <div className="flex items-center gap-1 px-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-200 ease-out">
            {showActions && (
              <button
                data-testid="ai-chat-copy-message-button"
                onClick={() => onCopy(message.id, message.content)}
                className="rp-advisor-focus text-xs text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 flex items-center gap-1 px-2 py-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800/70 transition-colors duration-150"
                aria-label={t('common.copy')}
                title={t('common.copy')}
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                <span className="hidden sm:inline">
                  {copied ? t('common.copied') : t('common.copy')}
                </span>
              </button>
            )}
            {showActions && isLastAssistant && (
              <button
                data-testid="ai-chat-regenerate-button"
                onClick={onRegenerate}
                className="rp-advisor-focus text-xs text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 flex items-center gap-1 px-2 py-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800/70 transition-colors duration-150"
                aria-label={t('aiAdvisor.regenerate')}
                title={t('aiAdvisor.regenerate')}
              >
                <RefreshCw className="w-3 h-3" />
                <span className="hidden sm:inline">{t('aiAdvisor.regenerate')}</span>
              </button>
            )}
            {message.error && (
              <button
                data-testid="ai-chat-retry-button"
                onClick={onRetry}
                className="rp-advisor-focus text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 flex items-center gap-1 px-2 py-1 rounded-md hover:bg-red-50 dark:hover:bg-red-950 transition-colors duration-150"
                aria-label={t('aiAdvisor.retry')}
              >
                <RefreshCw className="w-3 h-3" />
                <span>{t('aiAdvisor.retry')}</span>
              </button>
            )}
          </div>
        )}

        {featureIds.length > 0 && (
          <div
            data-testid="ai-chat-feature-chips"
            className="flex flex-wrap gap-1.5 pt-1.5"
            role="group"
            aria-label={t('aiAdvisor.chips.groupLabel')}
          >
            {featureIds.map((id) => (
              <button
                key={id}
                data-testid={`ai-chat-feature-chip-${id}`}
                onClick={() => handleOpenFeature(id)}
                className="rp-advisor-focus text-xs px-2.5 py-1.5 rounded-full bg-[#12355B]/5 dark:bg-[#45C04A]/10 border border-[#12355B]/30 dark:border-[#45C04A]/40 text-[#12355B] dark:text-[#45C04A] font-medium hover:bg-[#12355B]/10 dark:hover:bg-[#45C04A]/20 hover:-translate-y-0.5 active:translate-y-0 transition-[transform,background-color] duration-200 ease-out inline-flex items-center gap-1"
              >
                <ArrowUpRight className="w-3 h-3" aria-hidden="true" />
                {t(ADVISOR_FEATURES[id].labelKey)}
              </button>
            ))}
          </div>
        )}

        {!isUser && !message.streaming && !message.error && message.portfolioNudge && (
          <div
            data-testid="ai-chat-portfolio-nudge"
            className="pt-1.5"
          >
            <button
              data-testid="ai-chat-portfolio-nudge-cta"
              onClick={() => {
                // Save the *user's* original portfolio question so the
                // Advisor can auto-replay it as the first Premium query
                // after the user finishes checkout. The question sits
                // one turn above this assistant bubble (same conversation).
                const originalUserTurn = message.parentUserQuestion;
                if (originalUserTurn) {
                  savePendingQuestion({
                    question: originalUserTurn,
                    dealId,
                    address: advisorCtx?.address,
                  });
                }
                fireChatAnalytics('ai_advisor_upgrade_clicked', propertyKey || null, {
                  target: 'premium',
                  source: 'portfolio_nudge',
                });
                navigate('/pricing');
              }}
              className="rp-advisor-focus text-xs px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 border border-emerald-600 text-white font-medium hover:-translate-y-0.5 active:translate-y-0 transition-[transform,background-color] duration-200 ease-out inline-flex items-center gap-1.5 shadow-sm"
            >
              <Sparkles className="w-3 h-3" aria-hidden="true" />
              {t('aiAdvisor.portfolioNudge.cta')}
              <ArrowUpRight className="w-3 h-3" aria-hidden="true" />
            </button>
          </div>
        )}

        {showFollowups && (
          <div
            data-testid="ai-chat-followups"
            className="flex flex-wrap gap-1.5 pt-1"
            role="group"
            aria-label={t('aiAdvisor.followupLabel')}
          >
            {followups.map((q, idx) => (
              <button
                key={idx}
                data-testid={`ai-chat-followup-${idx}`}
                onClick={() => onFollowup && onFollowup(q)}
                className="rp-advisor-focus text-xs px-2.5 py-1.5 rounded-full bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 hover:border-[#12355B] dark:hover:border-[#45C04A] hover:bg-white dark:hover:bg-slate-800 hover:-translate-y-0.5 active:translate-y-0 text-slate-700 dark:text-slate-200 transition-[transform,border-color,background-color] duration-200 ease-out text-left"
              >
                {q}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(ChatMessage);
